import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, ChevronDown, ChevronRight, BookOpen, CheckCircle2 } from "lucide-react";
import {
  recommendationLabel,
  responsibilityLabel,
  aiPriorityLabel,
  type WorksPermitAnalysis,
  type MaintenanceAnalysis,
} from "@costadorada/shared";
import { cn } from "@/lib/cn";

type AnyAnalysis = WorksPermitAnalysis | MaintenanceAnalysis;
const isMaintenance = (a: AnyAnalysis): a is MaintenanceAnalysis => "responsibility" in a;

const recStyle: Record<string, string> = {
  APPROVE: "bg-green-100 text-green-800",
  APPROVE_WITH_CONDITIONS: "bg-amber-100 text-amber-800",
  REJECT: "bg-red-100 text-red-800",
  NEEDS_MORE_INFO: "bg-slate-200 text-slate-700",
};
const respStyle: Record<string, string> = {
  ASSOCIATION: "bg-blue-100 text-blue-800",
  OWNER: "bg-amber-100 text-amber-800",
  SHARED: "bg-violet-100 text-violet-800",
  UNCLEAR: "bg-slate-200 text-slate-700",
};
const prioStyle: Record<string, string> = {
  LOW: "bg-slate-100 text-slate-600",
  MEDIUM: "bg-blue-100 text-blue-800",
  HIGH: "bg-amber-100 text-amber-800",
  URGENT: "bg-red-100 text-red-800",
};

const chip = (cls: string, text: string) => (
  <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-bold", cls)}>{text}</span>
);

/** Published AI decision-support analysis — works for both works-permit (recommendation)
 *  and maintenance (responsibility + priority) requests. Content authored in Spanish;
 *  labels follow the app language. */
export function AiAnalysisPanel({ analysis, defaultOpen = true }: { analysis: AnyAnalysis; defaultOpen?: boolean }) {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);
  const [open, setOpen] = useState(defaultOpen);
  const L = (m: { es: string; en: string }) => (isEn ? m.en : m.es);

  const maint = isMaintenance(analysis);
  const listItems = maint ? analysis.recommendedActions : analysis.conditions;
  const listLabel = maint ? tr("Acciones recomendadas", "Recommended actions") : tr("Condiciones", "Conditions");

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-violet-900">
          <Sparkles size={16} /> {tr("Análisis de IA", "AI analysis")}
          {maint ? (
            <>
              {chip(respStyle[analysis.responsibility], L(responsibilityLabel[analysis.responsibility]))}
              {chip(prioStyle[analysis.suggestedPriority], L(aiPriorityLabel[analysis.suggestedPriority]))}
            </>
          ) : (
            chip(recStyle[analysis.recommendation], L(recommendationLabel[analysis.recommendation]))
          )}
        </span>
        {open ? <ChevronDown size={16} className="text-violet-700" /> : <ChevronRight size={16} className="text-violet-700" />}
      </button>

      {open && (
        <div className="space-y-3 px-3 pb-3 text-sm">
          <p className="text-slate-700">{analysis.summaryEs}</p>

          {analysis.reasoning.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-800">
                {tr("Razonamiento", "Reasoning")}
              </div>
              <ol className="space-y-1.5">
                {analysis.reasoning.map((s, i) => (
                  <li key={i} className="rounded border border-slate-200 bg-white px-2.5 py-1.5">
                    <div className="font-medium text-slate-800">{i + 1}. {s.titleEs}</div>
                    <div className="text-[13px] text-slate-600">{s.detailEs}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {listItems.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-800">{listLabel}</div>
              <ul className="space-y-1">
                {listItems.map((c, i) => (
                  <li key={i} className="flex gap-1.5 text-slate-700">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-green-600" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.citations.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-800">
                {tr("Artículos citados", "Cited articles")}
              </div>
              <div className="space-y-1.5">
                {analysis.citations.map((c, i) => (
                  <div key={i} className="rounded border border-slate-200 bg-white px-2.5 py-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <BookOpen size={13} className="shrink-0 text-primary" />
                      <span className="font-medium text-slate-800">{c.source}</span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600">{c.article}</span>
                    </div>
                    <div className="mt-0.5 text-[13px] text-slate-600">{c.relevance}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-violet-200 pt-2 text-[11px] text-slate-500">
            {analysis.documentsRead.length > 0 && (
              <div>{tr("Documentos leídos", "Documents read")}: {analysis.documentsRead.join(", ")}</div>
            )}
            <div>
              {analysis.engine === "claude"
                ? tr(`Generado por Claude${analysis.model ? ` (${analysis.model})` : ""}`, `Generated by Claude${analysis.model ? ` (${analysis.model})` : ""}`)
                : tr(
                    "Generado por el motor de reglas (configure la clave de Claude para un análisis que lea los documentos completos)",
                    "Generated by the rules engine (set the Claude key for an analysis that reads the full documents)"
                  )}
            </div>
            <div className="mt-1 italic">{analysis.disclaimerEs}</div>
          </div>
        </div>
      )}
    </div>
  );
}
